// Persistent client-side state for desktop-setup step 2 (roadmap #100 third
// follow-up). A web page cannot read the registry or the Startup folder, so the
// step's lifecycle is tracked with best-effort local flags per browser profile:
//
//   - downloaded: the setup file download was triggered here.
//   - confirmed:  the staff member pressed "I have run it".
//   - verified:   this window was launched with the ?autostart=1 marker that the
//                 setup file plants in the Startup shortcut's target URL - real
//                 proof the shortcut ran (highest precedence).
//
// Precedence: verified > confirmed > downloaded > pending. Known, accepted
// limitation: the setup file's -Uninstall option cannot clear these web-side
// flags, so the panel may keep showing the enabled state after an uninstall.
// Same `qr_` storage prefix as staffAuth's keys.

const DOWNLOADED_KEY = 'qr_setup_downloaded'
const CONFIRMED_KEY = 'qr_setup_confirmed'
const VERIFIED_KEY = 'qr_autostart_verified'

export type SetupState = 'pending' | 'downloaded' | 'confirmed' | 'verified'

const readFlag = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const writeFlag = (key: string): void => {
  try {
    localStorage.setItem(key, '1')
  } catch {
    // Storage unavailable (private mode / embedded webview): the state simply
    // does not persist; the UI falls back to the pending step on next load.
  }
}

export const getSetupState = (): SetupState => {
  if (readFlag(VERIFIED_KEY)) return 'verified'
  if (readFlag(CONFIRMED_KEY)) return 'confirmed'
  if (readFlag(DOWNLOADED_KEY)) return 'downloaded'
  return 'pending'
}

export const markSetupDownloaded = (): void => writeFlag(DOWNLOADED_KEY)
export const markSetupConfirmed = (): void => writeFlag(CONFIRMED_KEY)

// Captures the auto-start verification marker from the launch URL. MUST run in the
// app bootstrap (main.tsx) before React mounts: the staff sign-on round-trip
// returns to a fixed redirect URI (see staff/sso.ts) and would drop the query
// string, so the marker has to be persisted before any redirect can happen. After
// persisting, the parameter is stripped from the address bar (hash and any other
// query parameters preserved) so reloads and copied links stay clean.
export const captureAutostartMarker = (): void => {
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('autostart') !== '1') return
    writeFlag(VERIFIED_KEY)
    params.delete('autostart')
    const query = params.toString()
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    )
  } catch {
    // URL/history APIs unavailable: skip silently, the marker just is not recorded.
  }
}
